import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin, Instagram, Facebook, Music2, Loader2, CheckCircle2, Compass } from "lucide-react";
import Reveal from "@/components/Reveal";
import Mascot from "@/components/Mascot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socials = [
  { Icon: Instagram, label: "Instagram", handle: "@AardvarksVault", href: "https://instagram.com/AardvarksVault", id: "contact-social-instagram" },
  { Icon: Facebook, label: "Facebook", handle: "Aardvarks Vault", href: "https://facebook.com/AardvarksVault", note: "", id: "contact-social-facebook" },
  { Icon: Music2, label: "TikTok", handle: "@AardvarksVault", href: "https://tiktok.com/@AardvarksVault", id: "contact-social-tiktok" },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Contact — Aardvarks Vault";
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Your name is required.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = "A valid email is required.";
    if (!form.message.trim()) errs.message = "Tell us what's on your mind.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("message", form.message);
      await axios.post(`${API}/contact`, data);
      setSent(true);
      toast.success("Message sent — the Vault will reply soon.");
    } catch {
      toast.error("Couldn't send right now. Email ashton@aardvarksvault.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="px-5 pb-16 pt-40 sm:px-8 lg:pt-48" data-testid="contact-hero">
        <div className="mx-auto max-w-4xl text-center">
          <p className="eyebrow">Contact</p>
          <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl" data-testid="contact-hero-title">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
                Reach Ashton &
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block text-gold" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}>
                the Vault crew.
              </motion.span>
            </span>
          </h1>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_1.2fr]">
        <div className="space-y-6">
          <Reveal>
            <div className="card-frame foil-sheen p-8" data-testid="contact-details-card">
              <div className="flex items-center gap-4">
                <Mascot size={58} />
                <div>
                  <h2 className="font-serif text-xl font-semibold text-cream">The Vault Desk</h2>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mint">Replies within 24 hrs</p>
                </div>
              </div>
              <div className="mt-8 space-y-5 text-sm">
                <a href="mailto:ashton@aardvarksvault.com" data-testid="contact-email-link" className="flex items-center gap-3 text-cream/85 transition-colors hover:text-gold">
                  <Mail size={18} className="shrink-0 text-gold" /> ashton@aardvarksvault.com
                </a>
                <p className="flex items-center gap-3 text-cream/75">
                  <MapPin size={18} className="shrink-0 text-gold" /> Texas-based · Buying nationwide
                </p>
                <p className="flex items-center gap-3 text-cream/75">
                  <Compass size={18} className="shrink-0 text-gold" /> Selling a collection?{" "}
                  <Link to="/sell" className="text-mint underline-offset-4 hover:underline" data-testid="contact-sell-link">
                    Use the offer form
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="grid grid-cols-3 gap-4" data-testid="contact-socials-grid">
              {socials.map(({ Icon, label, handle, href, id }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={id}
                  className="card-frame foil-sheen group flex flex-col items-center gap-2 p-5 text-center transition-transform duration-300 hover:-translate-y-1"
                >
                  <Icon size={22} className="text-mint transition-colors group-hover:text-gold" />
                  <span className="text-xs font-semibold text-cream">{label}</span>
                  <span className="font-mono text-[9px] tracking-[0.12em] text-cream/55">{handle}</span>
                </a>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          {sent ? (
            <div className="card-frame foil-sheen flex h-full flex-col items-center justify-center p-12 text-center" data-testid="contact-form-success">
              <CheckCircle2 className="text-mint" size={48} />
              <h3 className="mt-5 font-serif text-xl font-bold text-cream">Message received</h3>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-cream/70">
                Thanks for reaching out — Ashton will reply to {form.email} shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="card-frame h-full p-8" data-testid="contact-form" noValidate>
              <h2 className="font-serif text-xl font-semibold text-cream">Send a message</h2>
              <p className="mt-2 text-sm text-cream/60">General questions, press, or VIP appraisal requests.</p>
              <div className="mt-7 space-y-5">
                <div>
                  <label htmlFor="cf-name" className="eyebrow mb-2 block">Name *</label>
                  <input id="cf-name" data-testid="contact-form-name-input" className="field" value={form.name} onChange={set("name")} placeholder="Your name" />
                  {errors.name && <p className="mt-2 text-xs text-red-400" data-testid="contact-form-name-error">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="cf-email" className="eyebrow mb-2 block">Email *</label>
                  <input id="cf-email" data-testid="contact-form-email-input" className="field" value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  {errors.email && <p className="mt-2 text-xs text-red-400" data-testid="contact-form-email-error">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="cf-message" className="eyebrow mb-2 block">Message *</label>
                  <textarea id="cf-message" data-testid="contact-form-message-input" className="field min-h-[140px] resize-y" value={form.message} onChange={set("message")} placeholder="How can the Vault help?" />
                  {errors.message && <p className="mt-2 text-xs text-red-400" data-testid="contact-form-message-error">{errors.message}</p>}
                </div>
                <button type="submit" className="btn-gold w-full sm:w-auto" disabled={sending} data-testid="contact-form-submit-button">
                  {sending ? <Loader2 className="animate-spin" size={16} /> : null}
                  {sending ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
